import { api } from "@/lib/api";
import type { Units } from "./units";

export type Weather = "DRY" | "WET";
export interface Corners<T = number> {
  lf: T;
  rf: T;
  lr: T;
  rr: T;
}
export const CORNER_KEYS = ["lf", "rf", "lr", "rr"] as const;
export type CornerKey = (typeof CORNER_KEYS)[number];

export interface GripAccount {
  plan: "FREE" | "PRO";
  status: string;
  isPro: boolean;
  /** True when Finding Grip is free for every account (no plans, no limits). */
  freeForAll: boolean;
  units: Units;
  currentPeriodEnd: string | null;
  cancelAtPeriodEnd: boolean;
  calculationsUsed: number;
  freeCalculationLimit: number;
  freeCalculationsLeft: number | null;
}

export interface GripMe {
  account: GripAccount;
  checkoutAvailable: boolean;
  calculatorAvailable: boolean;
}

export interface Track {
  id: string;
  name: string;
  shortName: string;
  country: string | null;
  logoUrl: string | null;
  isActive: boolean;
}

/** A car from the shared My cars list, as shown on a session or calculation. */
export interface CarRef {
  id: string;
  label: string | null;
  number: string;
  series: string;
}

export interface ReferenceSession {
  id: string;
  name: string;
  track: Track;
  car: CarRef | null;
  sessionDate: string;
  weather: Weather;
  trackTemp: number;
  ambientTemp: number;
  durationMin: number;
  cold: Corners;
  hot: Corners<number | null>;
  wheel: Corners;
  notes: string | null;
  useCount: number;
  ready: boolean;
  createdAt: string;
  updatedAt: string;
}

export interface SessionInput {
  name: string;
  trackId: string;
  weather: Weather;
  trackTemp: number;
  ambientTemp: number;
  durationMin: number;
  cold: Corners;
  hot?: Corners<number | null>;
  wheel: Corners;
  notes?: string | null;
  /** Shared My cars list; null for none. Left out: unchanged on edit. */
  userCarId?: string | null;
}

export interface Calculation {
  id: string;
  name: string;
  referenceSessionId: string | null;
  referenceName: string;
  track: Track;
  /** The reference session's car (shared My cars list). */
  car: CarRef | null;
  sessionDate: string;
  weather: Weather;
  trackTemp: number;
  ambientTemp: number;
  durationMin: number;
  wheel: Corners;
  target: Corners;
  result: Corners;
  referenceCold: Corners;
  convertedToRef: boolean;
  /** How the run came in, once the measured hot pressures are recorded. */
  outcome: Outcome | null;
  /** The driver said there is nothing to record for this run. */
  outcomeSkipped: boolean;
  createdAt: string;
}

export interface Outcome {
  /** Measured hot pressures (PSI). */
  hot: Corners;
  /** Measured minus target, per corner (PSI). */
  delta: Corners;
  onTarget: boolean;
  worstMiss: number;
  averageMiss: number;
  /** A corner within this many PSI of its target counts as on target. */
  onTargetPsi: number;
  recordedAt: string;
}

type TrackRef = Pick<Track, "id" | "name" | "shortName">;

/** How close the driver's recorded runs came (GET /grip/progress). Own runs only. */
export interface Progress {
  onTargetPsi: number;
  recordedTotal: number;
  recent: {
    count: number;
    onTarget: number;
    /** Average of each run's worst corner miss. */
    averageWorstMiss: number | null;
    /** Oldest first. */
    runs: {
      id: string;
      name: string;
      sessionDate: string;
      track: TrackRef;
      worstMiss: number;
      onTarget: boolean;
    }[];
  };
  /** The latest five runs' average worst-corner miss against the (up to) five before; null under six runs. */
  trend: {
    latestAverage: number;
    previousAverage: number;
    latestRuns: number;
    previousRuns: number;
    direction: "smaller" | "similar" | "larger";
  } | null;
  tracks: {
    track: TrackRef;
    recorded: number;
    onTarget: number;
    averageWorstMiss: number | null;
    lastDate: string;
  }[];
  /** Each car (shared My cars list); runs without a car are left out. */
  cars: {
    car: CarRef;
    recorded: number;
    onTarget: number;
    averageWorstMiss: number | null;
    lastDate: string;
  }[];
}

/** One calendar year in facts (GET /grip/season). Own runs only. */
export interface Season {
  year: number;
  onTargetPsi: number;
  calculations: number;
  referenceSessions: number;
  trackDays: number;
  recorded: number;
  onTarget: number;
  averageWorstMiss: number | null;
  tracks: {
    track: TrackRef;
    days: number;
    calculations: number;
    recorded: number;
    onTarget: number;
  }[];
  closest: {
    id: string;
    name: string;
    track: TrackRef;
    sessionDate: string;
    worstMiss: number;
    onTarget: boolean;
  } | null;
  /** "YYYY-MM-DD" (UTC day). */
  firstDate: string | null;
  lastDate: string | null;
}

export interface OutcomeInput {
  hot: Corners;
  saveAsReference?: boolean;
  referenceName?: string;
  notes?: string | null;
}

export interface CalculationInput {
  referenceSessionId: string;
  name: string;
  weather: Weather;
  trackTemp: number;
  ambientTemp: number;
  durationMin: number;
  wheel: Corners;
  target: Corners;
}

export interface DamperQuery {
  type: "rebound_only" | "two_way";
  direction: "left" | "right";
  over_under: "oversteer" | "understeer";
  segment: "braking" | "turn_in" | "mid_corner" | "corner_exit";
  speed: "low" | "mid" | "high";
}

export interface DamperResult {
  scenarioNumber: number;
  options: string[];
  scenarioCount: number;
}

export interface AdminOverview {
  counts: {
    accounts: number;
    pro: number;
    sessions: number;
    calculations: number;
    tracks: number;
  };
  setup: {
    access: "off" | "admin" | "testers" | "public";
    pricing: "free" | "paid";
    ownAddress: { url: string | null; problem: string | null };
    testerCount: number;
    calcModel: {
      ready: boolean;
      version: string | number | null;
      problem: string | null;
    };
    damperTable: { rows: number; expected: number; ready: boolean };
    stripePrice: { ready: boolean };
  };
}

export const gripApi = {
  status: () =>
    api.get<{ open: boolean; free: boolean; siteUrl?: string | null }>("/grip/status"),
  me: () => api.get<GripMe>("/grip/me"),
  setUnits: (units: Units) => api.put<{ account: GripAccount }>("/grip/me", { units }),
  tracks: () => api.get<{ tracks: Track[] }>("/grip/tracks").then((r) => r.tracks),

  sessions: () =>
    api.get<{ sessions: ReferenceSession[] }>("/grip/sessions").then((r) => r.sessions),
  session: (id: string) =>
    api.get<{ session: ReferenceSession }>(`/grip/sessions/${id}`).then((r) => r.session),
  createSession: (input: SessionInput) =>
    api
      .post<{ session: ReferenceSession }>("/grip/sessions", input)
      .then((r) => r.session),
  updateSession: (id: string, input: SessionInput) =>
    api
      .put<{ session: ReferenceSession }>(`/grip/sessions/${id}`, input)
      .then((r) => r.session),
  deleteSession: (id: string) => api.delete<void>(`/grip/sessions/${id}`),

  calculations: () =>
    api
      .get<{ calculations: Calculation[] }>("/grip/calculations")
      .then((r) => r.calculations),
  calculation: (id: string) =>
    api
      .get<{ calculation: Calculation }>(`/grip/calculations/${id}`)
      .then((r) => r.calculation),
  calculate: (input: CalculationInput) =>
    api.post<{ calculation: Calculation; account: GripAccount }>(
      "/grip/calculations",
      input
    ),
  /** Re-run a saved calculation with changed inputs and store the new result in its place. */
  updateCalculation: (id: string, input: CalculationInput) =>
    api.put<{ calculation: Calculation; account: GripAccount }>(
      `/grip/calculations/${id}`,
      input
    ),
  deleteCalculation: (id: string) => api.delete<void>(`/grip/calculations/${id}`),
  convert: (id: string, input: { name: string; hot: Corners; notes?: string | null }) =>
    api
      .post<{ session: ReferenceSession }>(`/grip/calculations/${id}/convert`, input)
      .then((r) => r.session),

  /** Record the hot pressures measured after the run (optionally also saving it as a reference). */
  recordOutcome: (id: string, input: OutcomeInput) =>
    api.post<{ calculation: Calculation; session: ReferenceSession | null }>(
      `/grip/calculations/${id}/outcome`,
      input
    ),
  /** "Didn't run it": stop asking how this one came in. */
  skipOutcome: (id: string) =>
    api
      .post<{ calculation: Calculation }>(`/grip/calculations/${id}/skip-outcome`)
      .then((r) => r.calculation),

  /** Accuracy over time: the last ten recorded runs, the trend, each track. */
  progress: () =>
    api.get<{ progress: Progress }>("/grip/progress").then((r) => r.progress),

  /** The season recap for one year (default: this year). */
  season: (year?: number) =>
    api
      .get<{ season: Season }>(`/grip/season${year ? `?year=${year}` : ""}`)
      .then((r) => r.season),

  damper: (q: DamperQuery) =>
    api.get<DamperResult>(`/grip/damper?${new URLSearchParams({ ...q }).toString()}`),

  checkout: () => api.post<{ url: string }>("/grip/billing/checkout"),
  portal: () => api.post<{ url: string }>("/grip/billing/portal"),

  admin: {
    overview: () => api.get<AdminOverview>("/grip/admin/overview"),
    tracks: () =>
      api.get<{ tracks: Track[] }>("/grip/admin/tracks").then((r) => r.tracks),
    addTrack: (input: { name: string; shortName: string }) =>
      api.post<{ track: Track }>("/grip/admin/tracks", input).then((r) => r.track),
    updateTrack: (
      id: string,
      input: { name: string; shortName: string; isActive?: boolean }
    ) =>
      api.put<{ track: Track }>(`/grip/admin/tracks/${id}`, input).then((r) => r.track),
    replaceDamperTable: (body: unknown) =>
      api.put<{ rows: number }>("/grip/admin/damper-scenarios", body),
  },
};

/** Download the user's data. Uses fetch directly because the response is a file, not JSON. */
export async function downloadExport(): Promise<void> {
  // Reading the response as text drops the byte-order mark, so put it back:
  // Excel needs it to read accented names correctly.
  const text = await api.getText("/grip/export");
  const url = URL.createObjectURL(
    new Blob(["\uFEFF", text], { type: "text/csv;charset=utf-8" })
  );
  const a = document.createElement("a");
  a.href = url;
  a.download = "finding-grip-export.csv";
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
}
